import { ChainType } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import {
  type Wallet as SolanaWallet,
  useWallet as useSolanaWallet,
} from '@solana/wallet-adapter-react'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import type {
  EcosystemWalletOptions,
  WalletOption,
  WalletOptionsProps,
} from '@/src/hooks/wallet/use-wallet-options'

export const useSolanaWalletOptions = ({
  searchWalletQuery,
  reportConnectingWalletInfo,
}: WalletOptionsProps = {}) => {
  const { ampli } = useAmplitudeAnalytics()
  const { wallets, connecting, disconnecting } = useSolanaWallet()
  const {
    wallet: solanaWallet,
    publicKey,
    select: selectSolanaWallet,
    disconnect,
  } = useSolanaWallet()
  const featureFlags = useAtomValue(featureFlagsAtom)
  const [shortCircuitReady, setShortCircuitReady] = useState(false)

  // Add timeout effect to resolve to ready after 2 seconds
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShortCircuitReady(true)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [])

  // Effect to disconnect OKX wallet if connected
  // OKX is known to have issues executing routes, see: https://linear.app/osmosis/issue/POLFE-285/filter-okx-from-cosmosusewalletoptions-and-remove-existing-connections
  useEffect(() => {
    if (
      solanaWallet?.adapter.name.includes('OKX') ||
      solanaWallet?.adapter.name.includes('MetaMask')
    ) {
      disconnect()
    }
  }, [solanaWallet, disconnect])

  const filteredSolanaWallets = useMemo(
    () =>
      wallets.filter(
        (wallet) =>
          wallet.readyState === 'Installed' &&
          !wallet.adapter.name.includes('OKX') &&
          !wallet.adapter.name.includes('MetaMask') &&
          (!searchWalletQuery ||
            wallet.adapter.name
              .toLowerCase()
              .includes(searchWalletQuery.toLowerCase())),
      ),
    [wallets, searchWalletQuery],
  )

  const connect = useCallback(
    ({
      wallet,
      onRetry,
      logEvent = true,
    }: {
      wallet: WalletOption
      onRetry?: () => Promise<void>
      logEvent?: boolean
    }) => {
      const solanaWallet = filteredSolanaWallets.find(
        (w) => w.adapter.name === wallet.id,
      )

      if (!solanaWallet) {
        throw new Error('Solana wallet not found')
      }

      return new Promise<void>((resolve, reject) => {
        const baseInfo = {
          icon: solanaWallet.adapter.icon,
          name: solanaWallet.adapter.name,
          type: ChainType.Solana,
        }
        reportConnectingWalletInfo?.({
          ...baseInfo,
          status: 'connecting',
        })
        const handleConnect = () => {
          if (logEvent && ampli) {
            ampli.walletConnectWalletCompleted({
              wallet: solanaWallet.adapter.name,
              type: 'solana',
            })
          }
          solanaWallet.adapter.off('connect', handleConnect)
          solanaWallet.adapter.off('error', handleError)
          resolve()
        }
        const handleError = (error: Error) => {
          reportConnectingWalletInfo?.({
            ...baseInfo,
            status: 'error',
            error: {
              type: (() => {
                if (error.message === 'User rejected the request.') {
                  return 'rejected'
                }

                return 'unexpected'
              })(),
              onRetry: () => {
                reportConnectingWalletInfo?.({
                  ...baseInfo,
                  status: 'connecting',
                })
                if (onRetry) {
                  return onRetry()
                }

                return connect({ wallet })
              },
            },
          })

          solanaWallet.adapter.off('connect', handleConnect)
          solanaWallet.adapter.off('error', handleError)
          reject(error)
        }

        solanaWallet.adapter.on('connect', handleConnect)
        solanaWallet.adapter.on('error', handleError)

        selectSolanaWallet(solanaWallet.adapter.name)
      })
    },
    [
      ampli,
      selectSolanaWallet,
      filteredSolanaWallets,
      reportConnectingWalletInfo,
    ],
  )

  const connectedWalletOption = useMemo(() => {
    /**
     * We also need to check for publickey, because solana wallet adapter populate "wallet" field,
     * even if you don't have it connected, for them it means that a wallet is available and selected for a possible connection
     */
    if (!solanaWallet || !publicKey) return undefined
    // Don't show OKX as connected option
    if (
      solanaWallet.adapter.name.includes('OKX') ||
      solanaWallet.adapter.name.includes('MetaMask')
    )
      return undefined
    return {
      ...makeWalletOption(solanaWallet),
      disconnect,
    }
  }, [solanaWallet, publicKey, disconnect])

  const connectableOptions = useMemo<
    EcosystemWalletOptions['connectableOptions']
  >(
    () =>
      filteredSolanaWallets.map((wallet) => {
        const walletOption = makeWalletOption(wallet)
        return {
          ...walletOption,
          connect: (params) => connect({ wallet: walletOption, ...params }),
        }
      }),
    [filteredSolanaWallets, connect],
  )

  return useMemo<EcosystemWalletOptions>(
    () => ({
      chainType: ChainType.Solana,
      ecosystemName: 'Solana',
      ecosystemImgUrl: '/assets/networks/solana.svg',
      connectableOptions,
      connectedWalletOption,
      enabled: featureFlags.solanaEcosystem,
      ready: shortCircuitReady || (!connecting && !disconnecting),
    }),
    [
      connectableOptions,
      connectedWalletOption,
      featureFlags.solanaEcosystem,
      shortCircuitReady,
      connecting,
      disconnecting,
    ],
  )
}

function makeWalletOption(wallet: SolanaWallet): WalletOption {
  let name = wallet.adapter.name as string

  // For solana, MetaMask appends a strange undetectable special character after it's name,
  // breaking logic downstream for aggregating wallets (like w EVM) by displayName.
  // This is a hack to fix that.
  if (name.toLowerCase().includes('metamask')) {
    name = 'MetaMask'
  }

  if (name.toLowerCase().includes('leap')) {
    name = 'Leap'
  }

  return {
    id: wallet.adapter.name,
    displayName: name.replace(/[^\w\s-]/g, ''),
    // Some wallets return logos with leading/trailing whitespace, need to trim
    // This will cause Next.js Image component to throw downstream
    logoUrl: (wallet.adapter.icon ?? '').trim(),
  }
}
